import React, { useMemo, useState } from 'react'

import { GiftIcon, PlusIcon } from 'lucide-react'

import { Box, Stack, Typography } from '@mui/material'

import { Alert, Button, Card, Skeleton, ToggleButton } from '@repo/ui'

// Direct subpaths rather than the `components` barrel: the barrel pulls in navigation,
// which reaches back into pages.
import { EmptyState } from 'components/display/EmptyState/EmptyState'
import { Progress } from 'components/display/Progress/Progress'
import { StatusChip } from 'components/display/StatusChip/StatusChip'
import { Table } from 'components/display/Table/Table'
import { headerWithTerm } from 'components/display/Table/headerWithTerm'
import { StickyFormHeader } from 'components/navigation/StickyFormHeader/StickyFormHeader'
import {
  OfferBlock,
  openReferABrandModal,
  ReferralLinkField,
} from 'components/feedback/modals/ReferABrandModal/ReferABrandModal'
import { REFERRAL_MEMBER_STATUSES } from 'constants/statuses'
import { formatCount } from 'constants/referrals'
import { useCanAccess } from 'hooks'
import {
  useMyReferralLink,
  useMyReferrals,
  useReferralSummary,
  type MemberReferral,
  type ReferralScope,
} from 'hooks/api/useReferrals'
import { useSettingsStickyOffset } from 'pages/settings/SettingsPage/settingsStickyOffset'
import { dateOnly } from 'utils'

const SCOPE_OPTIONS = [
  { label: 'Mine', value: 'mine' },
  { label: 'Company', value: 'company' },
]

/** Who the referral is FOR, in the most specific words we have, then the address. */
const prospectOf = (r: MemberReferral) => {
  const primary =
    r.referred_company_name ?? r.invitee.company_name ?? r.invitee.name ?? r.invitee.email ?? 'Shared link'
  const secondary = r.invitee.email && r.invitee.email !== primary ? r.invitee.email : null
  return { primary, secondary }
}

/**
 * VRG-655 — Settings → Personal → Refer & earn (`/settings/referrals`).
 *
 * Replaces the inert shell VRG-657 left after removing the FirstPromoter iframe (and with
 * it VRG-421's "reachable but nonfunctional" page).
 *
 * WHO SEES WHAT (Spencer, 2026-10-05):
 *  - every member: their own links and invites, each with its status and the activations it
 *    has brought in against the per-referral cap, plus the COMPANY totals with no names;
 *  - `manage_company` holders: a Mine / Company toggle that lists every member's referrals,
 *    with who sent each. The API enforces this (403 on `scope=company` without the
 *    permission); the toggle simply is not offered to anyone it would refuse.
 *
 * Credits are ACTIVATIONS, never dollars, and referral credits do not expire (VRG-649
 * decision 5) — so nothing on this page shows a value in money or an expiry date.
 */
export const ReferralsPage = () => {
  const stickyTop = useSettingsStickyOffset()
  const { isHasAccess: canSeeCompany } = useCanAccess({ action: 'manage_company' })
  const { isHasAccess: canSend } = useCanAccess({ action: 'referrals_send' })
  const [scope, setScope] = useState<ReferralScope>('mine')
  const effectiveScope: ReferralScope = canSeeCompany ? scope : 'mine'

  const summary = useReferralSummary()
  const list = useMyReferrals(effectiveScope)
  // Only for members who may send: `me/link` is gated on `referrals_send` and mints a code.
  const link = useMyReferralLink({ enabled: canSend })

  const columns = useMemo(
    () => [
      {
        field: 'prospect',
        headerName: 'Brand',
        flex: 1,
        minWidth: 160,
        sortable: false,
        valueGetter: (_: unknown, row: MemberReferral) => prospectOf(row).primary,
        renderCell: ({ row }: { row: MemberReferral }) => {
          const { primary, secondary } = prospectOf(row)
          return (
            <Stack justifyContent="center" sx={{ minWidth: 0, height: '100%' }}>
              <Typography variant="body2" noWrap>
                {primary}
              </Typography>
              {secondary && (
                <Typography variant="body4" color="text.secondary" noWrap>
                  {secondary}
                </Typography>
              )}
            </Stack>
          )
        },
      },
      ...(effectiveScope === 'company'
        ? [
            {
              field: 'sender',
              headerName: 'Sent by',
              width: 150,
              sortable: false,
              valueGetter: (_: unknown, row: MemberReferral) =>
                row.sender?.name ?? row.sender?.email ?? 'Former member',
            },
          ]
        : []),
      {
        // The date, with HOW it arrived underneath — one column rather than two, so the
        // company view (which adds "Sent by") still fits the settings pane at 1280 without
        // scrolling sideways.
        field: 'created_at',
        headerName: 'Date',
        width: 124,
        renderCell: ({ row }: { row: MemberReferral }) => (
          <Stack justifyContent="center" sx={{ height: '100%', minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {dateOnly(row.created_at) ?? '—'}
            </Typography>
            <Typography variant="body4" color="text.secondary" noWrap>
              {row.channel === 'email_invite' ? 'By email' : 'By link'}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 132,
        renderCell: ({ row }: { row: MemberReferral }) => (
          <StatusChip statuses={REFERRAL_MEMBER_STATUSES} status={row.status} size="small" />
        ),
      },
      {
        field: 'credits',
        headerName: 'Credits',
        // §49: sized for the HEADER plus its info glyph, not for the values.
        renderHeader: headerWithTerm('Credits', 'referral-credits'),
        width: 180,
        valueGetter: (_: unknown, row: MemberReferral) => row.credits.net,
        // The bar is progress toward the per-referral cap; the line under it is when this
        // referral stops adding credits (12 months from the first invoice that took money).
        renderCell: ({ row }: { row: MemberReferral }) => (
          <Stack justifyContent="center" spacing={1} sx={{ height: '100%', width: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={3}>
              <Box sx={{ flex: 1, minWidth: 40 }}>
                {/* A PERCENT on a 0-100 track. `Progress` hands `value` straight to MUI's
                    LinearProgress (which reads 0-100) and uses min/max only for its gradient,
                    so a raw 340 against max 1,000 drew the bar off its own track. */}
                <Progress value={(100 * row.credits.net) / Math.max(row.credits.cap, 1)} min={0} max={100} hideLabels />
              </Box>
              <Typography variant="body3" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                {formatCount(row.credits.net)} / {formatCount(row.credits.cap)}
              </Typography>
            </Stack>
            {row.reward_window_ends_at && (
              <Typography variant="body4" color="text.secondary" noWrap>
                Until {dateOnly(row.reward_window_ends_at)}
              </Typography>
            )}
          </Stack>
        ),
      },
    ],
    [effectiveScope],
  )

  const s = summary.data
  const tiles = s
    ? [
        { label: 'Your referrals', value: formatCount(s.mine.referrals) },
        { label: 'Your credits', value: formatCount(s.mine.credits_net) },
        { label: 'Company credits', value: formatCount(s.company.credits_net) },
        { label: 'Company paying brands', value: formatCount(s.company.paying_referrals) },
      ]
    : null

  return (
    <Box>
      <StickyFormHeader
        title="Refer & earn"
        nested
        top={stickyTop}
        actions={
          canSend ? (
            <Button
              variant="primary"
              size="medium"
              disableElevation
              startIcon={<PlusIcon size={16} strokeWidth={1} />}
              onClick={openReferABrandModal}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Invite a brand
            </Button>
          ) : undefined
        }
      >
        {canSeeCompany && (
          <ToggleButton
            tabs={SCOPE_OPTIONS}
            tab={scope}
            onChange={(v) => setScope(v === 'company' ? 'company' : 'mine')}
            sx={{ alignSelf: 'flex-start' }}
          />
        )}
      </StickyFormHeader>

      <Stack spacing={6}>
        {/* Totals: yours, then the company's. Company figures carry no names. */}
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
          }}
        >
          {tiles
            ? tiles.map((t) => (
                <Card key={t.label} sx={{ p: 4, mb: 0 }}>
                  <Typography variant="body3" color="text.secondary">
                    {t.label}
                  </Typography>
                  <Typography sx={{ fontSize: 28, lineHeight: 1.2, mt: 2, fontVariantNumeric: 'tabular-nums' }}>
                    {t.value}
                  </Typography>
                </Card>
              ))
            : [0, 1, 2, 3].map((i) => (
                <Card key={i} sx={{ p: 4, mb: 0 }}>
                  <Skeleton width={100} height={14} />
                  <Skeleton width={60} height={30} sx={{ mt: 2 }} />
                </Card>
              ))}
        </Box>

        {canSend && (
          <Card sx={{ p: 4, mb: 0 }}>
            <Stack spacing={5}>
              {link.data?.can_refer ? (
                <ReferralLinkField url={link.data.url ?? ''} label="Your link" />
              ) : link.isLoading ? (
                <Stack spacing={2}>
                  <Skeleton width={80} height={16} />
                  <Skeleton variant="rounded" height={49} />
                </Stack>
              ) : link.data && !link.data.can_refer ? (
                <Alert
                  severity="info"
                  title="Referrals are not available for this account."
                  description="Demo and example accounts cannot refer brands."
                />
              ) : null}
              {(link.data?.offer ?? s?.offer) && <OfferBlock offer={(link.data?.offer ?? s?.offer)!} />}
            </Stack>
          </Card>
        )}

        {list.isError ? (
          <Alert
            severity="error"
            title="We couldn't load your referrals."
            button={
              <Button variant="secondary" onClick={() => list.refetch()}>
                Try again
              </Button>
            }
          />
        ) : (
          <Box>
            <Table
              loading={list.isLoading}
              columns={columns}
              rows={list.data?.data ?? []}
              getRowId={(row: MemberReferral) => row.id}
              // Grows with the rows up to a cap; never shorter than the empty state needs
              // (at 260 its icon was clipped under the header).
              height={Math.max(380, Math.min(640, 56 + 72 * (list.data?.data.length ?? 4)))}
              showActionLine={false}
              showSelection={false}
              showFooter={(list.data?.total ?? 0) > 25}
              emptyState={
                <EmptyState
                  icon={<GiftIcon strokeWidth={1} size={32} />}
                  title="No referrals yet"
                  text={
                    effectiveScope === 'company'
                      ? 'When anyone at your company refers a brand, it shows up here with its status.'
                      : 'Brands you invite, and brands that join through your link, show up here with their status.'
                  }
                  buttons={
                    canSend ? (
                      <Button
                        variant="primary"
                        disableElevation
                        startIcon={<PlusIcon size={16} strokeWidth={1} />}
                        onClick={openReferABrandModal}
                      >
                        Invite a brand
                      </Button>
                    ) : undefined
                  }
                />
              }
            />
          </Box>
        )}
      </Stack>
    </Box>
  )
}
